import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Activity, RefreshCw, Loader2, ChevronRight } from 'lucide-react';
import { getIntegrationClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import type { EhrActivityCall } from '@vagusx/clients';

const REFRESH_INTERVAL_MS = 4000;

/**
 * Live view of the recent EHR API calls the org's connector has made (ADR-095).
 * Polls while open so staff can demo, in real time, which athenahealth APIs the
 * platform calls (book / open-slots / create-patient), including the host it
 * called and a redacted response body — expand a row to see them. PHI-free —
 * the connector redacts params and response bodies before they leave it.
 */
export function ConnectorActivitySheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: queryKeys.integrations.ehrActivity(),
    queryFn: () => getIntegrationClient().getEhrActivity(100),
    enabled: open,
    refetchInterval: open ? REFRESH_INTERVAL_MS : false,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <SheetTitle>EHR API activity</SheetTitle>
          </div>
          <SheetDescription className="mt-1">
            The most recent calls the connector made to the EHR. Updates live; no patient data is
            shown.
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !data || data.source === null ? (
            <EmptyState message="No EHR integration is connected for this organization." />
          ) : !data.supported ? (
            <EmptyState message={`The ${data.source} connector doesn't report its API activity.`} />
          ) : data.calls.length === 0 ? (
            <EmptyState message="No EHR calls recorded yet. Book or sync an appointment to see activity." />
          ) : (
            <ol className="space-y-1.5">
              {data.calls.map((call) => (
                <CallRow key={call.seq} call={call} />
              ))}
            </ol>
          )}
        </SheetBody>

        <SheetActions
          start={
            <Button
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-1.5"
            >
              {isFetching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Refresh
            </Button>
          }
        >
          <SheetClose asChild>
            <Button variant="outline">Close</Button>
          </SheetClose>
        </SheetActions>
      </SheetContent>
    </Sheet>
  );
}

function EmptyState({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
      <Activity className="h-8 w-8 text-muted-foreground/40" />
      <p className="max-w-xs text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function CallRow({ call }: { call: EhrActivityCall }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const params = { ...(call.query ?? {}), ...(call.form ?? {}) };
  const paramKeys = Object.keys(params);
  const hasDetail = Boolean(call.host) || call.responseBody !== undefined;

  return (
    <li className="rounded-md border px-3 py-2">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          disabled={!hasDetail}
          className="w-full text-left disabled:cursor-default"
        >
          <div className="flex items-center gap-2">
            {hasDetail && (
              <ChevronRight
                className={cn(
                  'h-3 w-3 shrink-0 text-muted-foreground transition-transform',
                  open && 'rotate-90'
                )}
              />
            )}
            <span
              className={cn(
                'shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold',
                methodClass(call.method)
              )}
            >
              {call.method}
            </span>
            <span className="flex-1 truncate font-mono text-xs" title={call.path}>
              {call.path}
            </span>
            <StatusBadge call={call} />
            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
              {call.durationMs}ms
            </span>
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 pl-5">
            <span className="truncate font-mono text-[10px] text-muted-foreground">
              {paramKeys.length > 0
                ? paramKeys.map((k) => `${k}=${String(params[k])}`).join('  ')
                : '—'}
            </span>
            <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
              {format(parseISO(call.startedAt), 'HH:mm:ss')}
            </span>
          </div>
        </CollapsibleTrigger>
        {hasDetail && (
          <CollapsibleContent className="mt-2 space-y-1.5 border-t pt-2 pl-5">
            {call.host && (
              <div className="font-mono text-[10px] text-muted-foreground">
                <span className="font-semibold text-foreground">Host: </span>
                {call.host}
              </div>
            )}
            {call.responseBody !== undefined && (
              <div>
                <div className="mb-1 text-[10px] font-semibold text-foreground">Response body</div>
                <pre className="max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[10px] text-muted-foreground">
                  {JSON.stringify(call.responseBody, null, 2)}
                </pre>
              </div>
            )}
          </CollapsibleContent>
        )}
      </Collapsible>
    </li>
  );
}

function StatusBadge({ call }: { call: EhrActivityCall }): React.ReactElement {
  const ok = call.outcome === 'success';
  const label = call.status === null ? (call.error ?? 'error') : String(call.status);
  return (
    <span
      className={cn(
        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
        ok ? 'bg-green-500/10 text-green-600' : 'bg-destructive/10 text-destructive'
      )}
    >
      {label}
    </span>
  );
}

function methodClass(method: string): string {
  switch (method) {
    case 'GET':
      return 'bg-blue-500/10 text-blue-600';
    case 'POST':
      return 'bg-green-500/10 text-green-600';
    case 'PUT':
      return 'bg-amber-500/10 text-amber-600';
    case 'DELETE':
      return 'bg-destructive/10 text-destructive';
    default:
      return 'bg-muted text-muted-foreground';
  }
}
